import { CommentItem, QueryParamsObject, Vendor, VendorRule } from "@finway-group/shared/lib/models"
import axios from "axios"

import { ReactQueryTableData } from "Shared/queries/vendors/types"
import { AuthzService, CostCenter2Service, CostCenterService, ExpenseAccountService } from "Shared/services"
import { isNotSet } from "Shared/utils/helper.utils"
import { queryGet } from "Shared/utils/reactQuery.utils"
import { VendorRuleForForm } from "Shared/utils/vendorRule.form.utils"

const MODEL = `vendors`

const VendorService = {
    updateOne: async (id: string, vendor: Vendor): Promise<Vendor> => {
        const { data } = await axios.put(`/${MODEL}/${id}`, { ...vendor })

        return new Vendor(data)
    },
    createOne: async (vendor: Vendor): Promise<Vendor> => {
        const { data } = await axios.post(`/${MODEL}`, vendor)

        return new Vendor(data)
    },
    checkForDuplicateVendors: async (newVendor: Partial<Vendor>) => {
        const { data } = await axios.post(`/vendors/check-duplicates`, newVendor)
        return data
    },
    fetchOneVendor: async (id: string) => {
        const { data } = await axios.get(`/vendors/${id}`)
        return new Vendor(data)
    },
    fetchOne: (id: string | undefined): Promise<Vendor> => (!id ? Promise.reject() : axios.get(`/${MODEL}/${id}`).then((response) => response.data)),
    deleteOne: async (id: string): Promise<void> => {
        await axios.delete(`/${MODEL}/${id}`)
    },
    updateComment: async (id: string, comments: Array<CommentItem>): Promise<Vendor> => {
        const vendorComments = { comments }
        const { data } = await axios.put(`/${MODEL}/${id}/comments`, vendorComments)
        return new Vendor(data)
    },
    updateVendorRule: async (id: string, vendorRule: VendorRule): Promise<Vendor> => {
        const rule = { rule: { ...vendorRule } }
        const { data } = await axios.patch(`/${MODEL}/${id}/rule`, rule)
        return new Vendor(data)
    },
    updateVendorContracts: async (id: string, contracts: Vendor["contracts"]): Promise<Vendor> => {
        const { data } = await axios.patch(`/${MODEL}/${id}/contracts`, { contracts })

        return new Vendor(data)
    },
    approveVendorStatus: async (id: string): Promise<Vendor> => {
        const { data } = await axios.put(`/${MODEL}/${id}/approve`)

        return new Vendor(data)
    },
    fetchAllVendors: async (queryParams: QueryParamsObject, signal?: AbortSignal): Promise<ReactQueryTableData<Vendor>> => {
        const data = await queryGet<ReactQueryTableData<Vendor>>(`${MODEL}`, { signal, params: queryParams })
        return { ...data, docs: data.docs.map((vendor: any) => new Vendor(vendor)) }
    },
    getLatestVendorCreditorNumber: async () => {
        const {
            data: { creditorNumberSequence },
        } = await axios.get("/vendors/sequence/latest-creditor-number")
        return creditorNumberSequence
    },
    export: async () => {
        await axios.post("/vendors/export")
    },
    syncExpenses: async (id: string) => {
        const { data } = await axios.post(`/vendors/${id}/sync-expenses`)
        return data
    },
    /**
     * If vendor creation is restricted that means having:
     *  - VENDOR__ALL__CREATE -> can create a directly approved vendor (button is shown)
     *  - VENDOR__ALL__CREATE_UNAPPROVED -> can create a vendor, but will be unapproved (button is shown)
     *  - None -> cannot open the form
     *
     * If vendor creation is not restricted, then
     *  - VENDOR__ALL__CREATE -> can create a directly approved vendor (button is shown)
     *  - VENDOR__ALL__CREATE_UNAPPROVED -> can create a directly approved vendor (button is shown)
     *  - None -> cannot open the form
     */
    shouldShowVendorButton: (isVendorCreationRestricted: boolean) =>
        AuthzService.isAllowedToCreateVendors() || (!isVendorCreationRestricted && AuthzService.isAllowedToCreateUnapprovedVendors()),
    getInitialVendorFormRule: (vendor: Vendor | undefined): VendorRuleForForm => ({
        costCenter: CostCenterService.getCostCenterById(vendor?.rule?.costCenter)?._id ?? "",
        costCenter2: CostCenter2Service.getCostCenter2ById(vendor?.rule?.costCenter2)?._id ?? "",
        expenseAccount: ExpenseAccountService.getExpenseAccountById(vendor?.rule?.expenseAccount)?._id ?? "",
        expensePaymentFlowInformation: vendor?.rule?.expensePaymentFlowInformation ?? "",
        paymentTerm: vendor?.rule?.paymentTerm,
        discounts: vendor?.rule?.discounts?.length ? vendor.rule.discounts : [{ period: undefined, percentage: undefined }],
    }),
    findBestVendorMatch: async ({ name, iban }: Partial<Pick<Vendor, "name" | "iban">>) => {
        if (isNotSet(name) && isNotSet(iban)) {
            return
        }
        const { data } = await axios.post(`${MODEL}/best-match`, { name, iban })
        if (!data) {
            return
        }
        return new Vendor(data)
    },
}

export default VendorService
